import type { DbUser } from "@prisma/client";

import { Avatar, AvatarFallback } from "@/ui/avatar";
import { cn } from "@/utils/tailwind";

interface ProfileAvatarProps {
  dbUser: DbUser;
  className?: string;
}

export function ProfileAvatar({ dbUser, className }: ProfileAvatarProps) {
  return (
    <Avatar className={className}>
      <AvatarFallback>
        <ProfileDefaultAvatar colors={dbUser.avatarColors} />
      </AvatarFallback>
    </Avatar>
  );
}

interface ProfileDefaultAvatarProps {
  colors: number[];
  className?: string;
}

export function ProfileDefaultAvatar({
  colors,
  className,
}: ProfileDefaultAvatarProps) {
  // A color stop is defined by an HSL color value and a position percentage.
  // Each color stop specifies a color and its exact position within the
  // gradient's length.
  //
  const fromColorStop = `hsl(${colors[0]}, ${colors[1]}%, ${colors[2]}%) ${colors[3]}%`;
  const viaColorStop = `hsl(${colors[4]}, ${colors[5]}%, ${colors[6]}%) ${colors[7]}%`;
  const toColorStop = `hsl(${colors[8]}, ${colors[9]}%, ${colors[10]}%) ${colors[11]}%`;

  return (
    <div
      className={cn("rounded-full", className ?? "size-full")}
      style={{
        background: `linear-gradient(to bottom right, ${fromColorStop}, ${viaColorStop}, ${toColorStop})`,
      }}
    />
  );
}

export function generateAvatarColors(str: string) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer.
  }
  const seed = Math.abs(hash);

  const hue1 = seed % 360;
  const saturation1 = 30 + Math.floor(Math.random() * 50);
  const lightness1 = 40 + Math.floor(Math.random() * 40);
  const percent1 = 0 + Math.floor(Math.random() * 30);

  const hue2 = (hue1 + 20 + Math.floor(Math.random() * 50)) % 360;
  const saturation2 = 30 + Math.floor(Math.random() * 50);
  const lightness2 = 40 + Math.floor(Math.random() * 40);
  const percent2 = percent1 + 20 + Math.floor(Math.random() * (60 - percent1));

  const hue3 = (hue2 + 20 + Math.floor(Math.random() * 50)) % 360;
  const saturation3 = 30 + Math.floor(Math.random() * 50);
  const lightness3 = 40 + Math.floor(Math.random() * 40);
  const percent3 = percent2 + 20 + Math.floor(Math.random() * (80 - percent2));

  return [
    hue1,
    saturation1,
    lightness1,
    percent1,

    hue2,
    saturation2,
    lightness2,
    percent2,

    hue3,
    saturation3,
    lightness3,
    percent3,
  ];
}
